import type { ReactNode } from "react";
import { useRef, useState } from "react";

type TooltipPosition = "right" | "left" | "top" | "bottom";

interface TooltipTestProps {
    children: ReactNode;
    content: ReactNode;
    placement?: TooltipPosition;
    delay?: number;
    className?: string;
    tooltipClassName?: string;
}

export function TooltipComponent({ children, content, tooltipClassName, className, placement = "top", delay = 200}: TooltipTestProps) {
    const [open, setOpen] = useState(false);
    const timeRef = useRef<number | null>(null);

    const handleMouseEnter = () => {
        timeRef.current = window.setTimeout(() => {
            setOpen(true);
        }, delay);
    };

    const handleMouseLeave = () => {
        if (timeRef.current) {
            clearTimeout(timeRef.current);
            timeRef.current = null;
        }
        setOpen(false);
    };

    return (
        <div
            className={`relative inline-block ${className || ""}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}

            {open && (
                <span
                    className={`
                        absolute
                        z-[999]
                        max-w-[300px]
                        rounded-lg
                        bg-[#111]
                        px-[10px] py-[8px]
                        whitespace-normal
                        pointer-events-none
                        transition-opacity
                        duration-150
                        ease-in-out
                        group-hover:opacity-100

                        !w-[250px]
                        !text-white
                        !font-['DM_Sans',sans-serif]
                        !transition-[background,color] duration-[400ms] ease-[cubic-bezier(1,0,0.4,1)]
                        !p-[15px] !rounded-[16px]
                        [--c-glass:#313030f8]
                        [--c-light:#fff]
                        [--c-dark:#000]
                        [--c-content:#224]
                        [--c-action:#0052f5]
                        [--c-bg:#e8e8e9]
                        [--glass-reflex-dark:1]
                        [--glass-reflex-light:1]
                        [--saturation:150%]
                        backdrop-saturate-[var(--saturation)]
                        [-webkit-backdrop-filter:blur(8px)_saturate(var(--saturation))]
                        !shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--c-light)_calc(var(--glass-reflex-light)*10%),transparent),inset_6px_6px_6px_-6px_color-mix(in_srgb,var(--c-light)_calc(var(--glass-reflex-light)*90%),transparent),inset_-4px_-4px_0px_-2px_color-mix(in_srgb,var(--c-light)_calc(var(--glass-reflex-light)*80%),transparent),inset_-3px_-8px_1px_-6px_color-mix(in_srgb,var(--c-light)_calc(var(--glass-reflex-light)*60%),transparent),inset_-0.3px_-1px_4px_0px_color-mix(in_srgb,var(--c-dark)_calc(var(--glass-reflex-dark)*12%),transparent),inset_-1.5px_2.5px_0px_-2px_color-mix(in_srgb,var(--c-dark)_calc(var(--glass-reflex-dark)*20%),transparent),inset_0px_3px_4px_-2px_color-mix(in_srgb,var(--c-dark)_calc(var(--glass-reflex-dark)*20%),transparent),inset_2px_-6.5px_1px_-4px_color-mix(in_srgb,var(--c-dark)_calc(var(--glass-reflex-dark)*10%),transparent),0px_1px_5px_0px_color-mix(in_srgb,var(--c-dark)_calc(var(--glass-reflex-dark)*10%),transparent),0px_6px_16px_0px_color-mix(in_srgb,var(--c-dark)_calc(var(--glass-reflex-dark)*8%),transparent)]
                        !bg-[color-mix(in_srgb,var(--c-glass)_88%,transparent)]

                        ${placement === "right" && "top-1/2 left-[105%] -translate-y-1/2"}
                        ${placement === "left" && "top-1/2 right-[105%] -translate-y-1/2"}
                        ${placement === "top" && "bottom-[110%] left-1/2 -translate-x-1/2"}
                        ${placement === "bottom" && "top-[110%] left-1/2 -translate-x-1/2"}
                        ${tooltipClassName}
                    `}
                >
                    {content}
                </span>
            )}
        </div>
    );
}